import { COMMAND_CENTER_ROOT, commandCenterPath } from './commandCenterRoutes'
import { useNavigate } from 'react-router-dom'
import { useCommandDispatch } from '../../hooks/useCommandDispatch'
import { AGENT_PREFILL_KEY } from '../../shell/agentPrefill'
import { OperationsAgentCommandCenter, useOperationsApprovalQueue } from './operations/agent-command'

/**
 * V2 Agent Command — route wrapper.
 *
 * Registered standalone + auth-gated (see router.tsx):
 *   /square/command-center/operations/agent-command
 *
 * Owns the two live hooks: useCommandDispatch (the SAME dispatcher every
 * other surface uses — no new command router) and
 * useOperationsApprovalQueue (a read-only view of the existing agent
 * approval queue). The workroom itself is presentational + SSR-safe.
 * Approving/rejecting a request stays on the Agent Workspace surface.
 */
export default function SquareCodeAgentCommandRoute() {
  const navigate = useNavigate()
  const { state, execute, dispatchProposal, clear } = useCommandDispatch()
  const { rows, loading, error, authRequired, reload } = useOperationsApprovalQueue()

  const sendToAgent = (text: string) => {
    const request = (text || '').trim()
    if (request) {
      try {
        window.sessionStorage.setItem(AGENT_PREFILL_KEY, request)
      } catch {
        /* sessionStorage unavailable — navigation still lands the operator in the lane */
      }
    }
    clear()
    navigate('/square/command-center/agents')
  }

  const runProposal = () => {
    if (state.interpretation?.kind !== 'proposal') return
    void dispatchProposal(state.interpretation.intent)
  }

  return (
    <OperationsAgentCommandCenter
      dispatch={state}
      onExecute={(input) => void execute(input)}
      onRunProposal={runProposal}
      onClear={clear}
      onSendToAgent={sendToAgent}
      queue={rows}
      queueLoading={loading}
      queueError={error}
      queueAuthRequired={authRequired}
      onReloadQueue={reload}
      onHome={() => navigate(COMMAND_CENTER_ROOT)}
      onBackCommandCenter={() => navigate(COMMAND_CENTER_ROOT)}
      onBackOperations={() => navigate(commandCenterPath('operations'))}
      onOpenRoute={(to) => navigate(to)}
    />
  )
}
